// SessionStorage-backed wizard state. Each signup step persists what the
// next step needs (pendingSignupId + GSTIN echo + quote totals + order id).
// Tab close clears it automatically — no leaks across browser sessions.
//
// Storage rules:
//   SAFE to persist        : pendingSignupId, gstin, contact{Name,Email,Mobile},
//                             userCount, billingCycle, quote, order
//                             (razorpay PUBLISHABLE key + order id only),
//                             masterUsername, status (server strips codes),
//                             provisioned (post-success summary).
//   NEVER persist          : masterPassword, password, razorpaySignature,
//                             OTP codes, accessToken/refreshToken/JWT,
//                             razorpay key SECRET, webhook secret.
//
// `patchSignupState` runs every patch through a sanitizer that drops any
// field whose key matches the forbidden set. This is defense in depth: the
// callsites are correct today, but a future refactor might accidentally
// spread an object that contains a password — the sanitizer catches it.

const KEY = 'glassi.signup';

// Lower-cased for case-insensitive matching. Keep this list as broad as
// the codebase: any time a field name COULD be confused with a secret,
// add it here. A redundant entry is cheap; a missing one leaks credentials.
const FORBIDDEN_KEYS = new Set([
  'masterpassword',
  'password',
  'newpassword',
  'oldpassword',
  'razorpaysignature',
  'razorpay_signature',
  'signature',
  'otp',
  'mobileotp',
  'emailotp',
  'mobileotpcode',
  'emailotpcode',
  'accesstoken',
  'refreshtoken',
  'jwt',
  'jwttoken',
  'apikey',
  'razorpaykeysecret',
  'razorpay_key_secret',
  'keysecret',
  'razorpaywebhooksecret',
  'razorpay_webhook_secret',
  'webhooksecret',
  'secret'
]);

function isForbidden(key) {
  if (typeof key !== 'string') return false;
  return FORBIDDEN_KEYS.has(key.toLowerCase());
}

// Recursive sanitizer — handles nested objects (e.g. an accidentally
// passed { credentials: { masterPassword: '...' } }). Arrays are walked
// element-by-element and forbidden child fields inside objects are
// dropped. Primitives pass through as-is.
function sanitize(value) {
  if (value == null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sanitize);
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (isForbidden(k)) {
      // Don't echo the value — only the key — to avoid logging secrets.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[signupStore] dropped forbidden key from patch:', k);
      }
      continue;
    }
    out[k] = sanitize(v);
  }
  return out;
}

function read() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

function write(state) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(state || {}));
  } catch (_e) {
    /* noop */
  }
}

export function getSignupState() {
  return read();
}

export function patchSignupState(patch) {
  const cleaned = sanitize(patch);
  const next = { ...read(), ...(cleaned || {}) };
  write(next);
  return next;
}

export function clearSignupState() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (_e) {
    /* noop */
  }
}
